// The replay rail: a timeline control that IS the activity chart.
//
// Replay alone cannot answer "when is this team busy" — you would have to
// sit through twenty days to find the peaks. So the control is also the
// histogram: the shape of the window is visible before you play it, and a
// peak is somewhere you can SEE and click rather than somewhere you have
// to arrive at.
//
// Bars are EVENTS PER LOCAL HOUR. Local, not UTC: on measured data this
// team's whole working day falls between 20:00 and 04:00 UTC, so a UTC
// rail would draw the team day as a dead block in the middle of the night.
//
// All of the geometry is in rail.ts, because this project tests pure
// functions and has no DOM test environment. What is left here is drawing.

import { useMemo, useRef } from 'react';
import type { HourBucket, QuietGap } from '../battlemap/replay';
import {
  railPaths, backwardsTicks, localDayTicks, fractionAt, instantAtFraction, pointerFraction,
  RAIL_VIEW_H, RAIL_COLOURS, RAIL_LABELS,
} from '../battlemap/rail';
import { PLAYBACK_SPEEDS } from '../battlemap/playback';

export interface ReplayRailProps {
  playing: boolean;
  speed: number;
  onTogglePlay: () => void;
  onSpeed: (n: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onFirst: () => void;
  onLast: () => void;
  buckets: HourBucket[];
  gaps: QuietGap[];
  fromMs: number;
  toMs: number;
  playheadMs: number;
  onScrub: (ms: number) => void;
  /** How many events have happened up to and including the playhead. */
  eventsBefore: number;
  eventCount: number;
  /** Local label for an instant, e.g. "Wed 24 Sep 09:00". */
  formatInstant: (ms: number) => string;
  /** Local day label for an instant, e.g. "24 Sep". */
  formatDay: (ms: number) => string;
}

export function ReplayRail({
  playing, speed, onTogglePlay, onSpeed, onPrev, onNext, onFirst, onLast,
  buckets, gaps, fromMs, toMs, playheadMs, onScrub,
  eventsBefore, eventCount, formatInstant, formatDay,
}: ReplayRailProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragging = useRef(false);

  const { series, peak } = useMemo(() => railPaths(buckets), [buckets]);
  // Backwards moves, as red ticks over the hour they happened in. Rare
  // enough that they should be the thing you notice.
  const backTicks = useMemo(() => backwardsTicks(buckets), [buckets]);
  const dayTicks = useMemo(() => localDayTicks(buckets), [buckets]);

  const n = buckets.length || 1;
  const playFrac = fractionAt(playheadMs, fromMs, toMs);

  const scrub = (clientX: number) => {
    const el = trackRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    onScrub(instantAtFraction(pointerFraction(clientX, r.left, r.width), fromMs, toMs));
  };

  return (
    <div className="select-none px-4 pb-2 pt-1">
      <div className="mb-1 flex items-center gap-3 text-xs">
        <span className="tabular-nums font-medium text-ink">{formatInstant(playheadMs)}</span>
        <span className="tabular-nums text-ink-dim">
          event {eventsBefore} of {eventCount}
        </span>
        <span className="tabular-nums text-ink-dim">peak {peak}/h</span>
        {/* The window is the campaign, always. Saying so means the rail is
            never mistaken for a freely-settable range, which is what it
            used to be and where three scoping bugs came from. */}
        <span className="tabular-nums text-ink-dim">
          campaign · {Math.max(1, Math.round((toMs - fromMs) / 86_400_000))}d
        </span>
        <span className="inline-flex items-center gap-1">
          <button type="button" onClick={onFirst} title="Back to the start"
            className="rounded border border-white/15 px-1.5 py-0.5 text-ink-muted hover:bg-white/10">⏮</button>
          <button type="button" onClick={onPrev} title="Previous event"
            className="rounded border border-white/15 px-1.5 py-0.5 text-ink-muted hover:bg-white/10">⏴</button>
          {/* STEP and PLAY need different glyphs. They were both a solid
              ▶, which is two different actions wearing the same face: one
              advances a single event with no animation, the other runs the
              replay. Choosing wrong looks like the animations are broken,
              because stepping once to a piece that does not move is
              exactly what "nothing happened" looks like.
              ⏴ ⏵ step, ▶ ⏸ play. */}
          <button type="button" onClick={onTogglePlay} title={playing ? 'Pause' : 'Play'}
            className="rounded border border-white/25 bg-white/10 px-2 py-0.5 font-semibold text-ink hover:bg-white/20">
            {playing ? '⏸' : '▶'}
          </button>
          <button type="button" onClick={onNext} title="Next event"
            className="rounded border border-white/15 px-1.5 py-0.5 text-ink-muted hover:bg-white/10">⏵</button>
          <button type="button" onClick={onLast} title="Jump to now"
            className="rounded border border-white/15 px-1.5 py-0.5 text-ink-muted hover:bg-white/10">⏭</button>
          {/* Events per second, not hours per second. That is the rate the
              operator can reason about: it is what governs how fast a
              comment flashes past. */}
          <select
            value={speed}
            onChange={(e) => onSpeed(Number(e.target.value))}
            className="ml-1 rounded border border-white/15 bg-transparent px-1 py-0.5 text-ink-muted"
            title="Events per second"
          >
            {PLAYBACK_SPEEDS.map((n) => (
              <option key={n} value={n}>{n}/s</option>
            ))}
          </select>
        </span>

        {/* The three kinds, in the SAME colours as the indicators on the
            board: a magenta band here and a magenta diamond over a piece
            are the same event. */}
        <span className="ml-auto inline-flex items-center gap-3 text-ink-dim">
          {series.map((s) => (
            <span key={s.name} className="inline-flex items-center gap-1">
              <span
                className="inline-block h-2 w-2 rounded-sm"
                style={{ background: RAIL_COLOURS[s.name] }}
              />
              {RAIL_LABELS[s.name]}
            </span>
          ))}
        </span>
      </div>

      <div
        ref={trackRef}
        className="relative h-20 cursor-ew-resize touch-none rounded border border-white/10 bg-black/40"
        onPointerDown={(e) => {
          dragging.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          scrub(e.clientX);
        }}
        onPointerMove={(e) => { if (dragging.current) scrub(e.clientX); }}
        onPointerUp={(e) => {
          dragging.current = false;
          e.currentTarget.releasePointerCapture(e.pointerId);
        }}
      >
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox={`0 0 ${n} ${RAIL_VIEW_H}`}
          preserveAspectRatio="none"
          aria-hidden
        >
          {series.map((s) => (
            <path key={s.name} d={s.d} fill={RAIL_COLOURS[s.name]} fillOpacity="0.78" />
          ))}
          {backTicks ? (
            <path d={backTicks} stroke="#ff4d4f" strokeWidth="1.6" strokeOpacity="0.95" fill="none" />
          ) : null}
        </svg>

        {/* Quiet runs are LABELLED rather than left as empty space. On this
            data the empty space is the interesting part: it is the boundary
            between one working day and the next. */}
        {gaps.map((g) => {
          const left = fractionAt(g.fromMs, fromMs, toMs) * 100;
          const width = fractionAt(g.toMs, fromMs, toMs) * 100 - left;
          if (width < 6) return null;
          return (
            <div
              key={g.fromMs}
              className="pointer-events-none absolute top-0 flex h-full items-center justify-center overflow-hidden"
              style={{ left: `${left}%`, width: `${width}%` }}
            >
              <span className="whitespace-nowrap text-[10px] text-ink-dim">{g.hours}h quiet</span>
            </div>
          );
        })}

        <div
          className="pointer-events-none absolute top-0 h-full w-px bg-white/80"
          style={{ left: `${playFrac * 100}%` }}
        />

        {/* Day ticks. Positioned as a percentage so they stay aligned with
            the stretched SVG above them. */}
        {dayTicks.map((i) => (
          <div
            key={i}
            className="pointer-events-none absolute top-0 h-full border-l border-white/15"
            style={{ left: `${(i / n) * 100}%` }}
          >
            <span className="absolute bottom-0.5 whitespace-nowrap pl-1 text-[10px] text-ink-dim">
              {formatDay(buckets[i]!.startMs)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
